/**
 * Scale an ingredient amount based on the number of servings
 * @param {number} baseAmount - Original amount from recipe
 * @param {number} baseServings - Original number of servings
 * @param {number} newServings - Desired number of servings
 * @returns {number} Scaled amount
 */
export const scaleIngredientAmount = (baseAmount, baseServings, newServings) => {
  if (!baseServings || baseServings === 0) return baseAmount;
  const scaleFactor = newServings / baseServings;
  return baseAmount * scaleFactor;
};

/**
 * Format a decimal number as a fraction with Unicode fraction characters
 * @param {number} decimal - Decimal number to format
 * @returns {string} Formatted fraction string
 */
export const formatFraction = (decimal) => {
  // Common fractions with Unicode characters
  const fractions = {
    0.125: '⅛',
    0.25: '¼',
    0.333: '⅓',
    0.375: '⅜',
    0.5: '½',
    0.625: '⅝',
    0.667: '⅔',
    0.75: '¾',
    0.875: '⅞'
  };

  const wholePart = Math.floor(decimal);
  const fractionalPart = decimal - wholePart;

  // Find closest fraction (within 0.01 tolerance)
  let closestFraction = null;
  let closestDiff = Infinity;

  Object.keys(fractions).forEach(key => {
    const diff = Math.abs(parseFloat(key) - fractionalPart);
    if (diff < closestDiff) {
      closestDiff = diff;
      closestFraction = key;
    }
  });

  // If we found a close match (within 1% tolerance)
  if (closestDiff < 0.01 && closestFraction) {
    if (wholePart > 0) {
      return `${wholePart} ${fractions[closestFraction]}`;
    }
    return fractions[closestFraction];
  }

  // If no close fraction match, return decimal with 2 decimal places
  // Remove trailing zeros
  return decimal.toFixed(2).replace(/\.?0+$/, '');
};

/**
 * Convert amount from one unit to another
 * @param {number} amount - Amount to convert
 * @param {string} fromUnit - Source unit abbreviation
 * @param {string} toUnit - Target unit abbreviation
 * @param {Array} unitsData - Units of measure data from API
 * @returns {number} Converted amount
 */
export const convertUnit = (amount, fromUnit, toUnit, unitsData) => {
  if (fromUnit === toUnit) return amount;

  // This is a simplified implementation
  // The actual implementation will depend on the structure of the units API response
  // For now, return the original amount
  // TODO: Implement unit conversion based on actual API response structure

  return amount;
};

/**
 * Get compatible units for conversion based on unit type
 * @param {string} currentUnit - Current unit abbreviation
 * @param {Array} unitsData - Units of measure data from API
 * @returns {Array} Array of compatible units
 */
export const getCompatibleUnits = (currentUnit, unitsData) => {
  if (!unitsData || unitsData.length === 0) return [];

  // Find the current unit in the data
  const current = unitsData.find(u => u.abbreviation === currentUnit);
  if (!current) return [{ abbreviation: currentUnit, name: currentUnit }];

  // Return units of the same type (volume, weight, etc.)
  // This is a simplified implementation
  // TODO: Implement based on actual API response structure

  return unitsData.filter(u => u.type === current.type);
};
